import { BillingInterval, SubscriptionPlan } from "@/server/types/subscription";

export interface Plan {
  id: SubscriptionPlan;
  name: string;
  tagline: string;
  monthlyPrice: number;
  yearlyPrice: number;
  yearlyMonthlyEquiv: number;
  savingsPercent: number;
  annualSavings: number;
  architectCredits: string;
  features: string[];
}

type PlanConfig = Omit<Plan, "annualSavings">;

export const PLANS: Plan[] = (
  [
    {
      id: "free",
      name: "Free",
      tagline:
        "Start with secure messaging and essential ORION Architect access.",
      monthlyPrice: 0,
      yearlyPrice: 0,
      yearlyMonthlyEquiv: 0,
      savingsPercent: 0,
      architectCredits: "60 trial credits",
      features: [
        "Group chats up to 500",
        "Voice notes 60 sec",
        "Group video calls up to 10 participants",
        "End-to-end encryption",
      ],
    },
    {
      id: "standard",
      name: "Standard",
      tagline: "For casual users who want solid messaging with occasional OA.",
      monthlyPrice: 22.22,
      yearlyPrice: 199.99,
      yearlyMonthlyEquiv: 16.66,
      savingsPercent: 25,
      architectCredits: "222 credits/mo",
      features: [
        "Polymath BFN™ Calculator included",
        "Groups up to 1,250",
        "Voice notes 3 min",
        "File sharing up to 100 MB",
      ],
    },
    {
      id: "pro",
      name: "Pro",
      tagline:
        "For users who turn to OA regularly and need meaningful credit depth.",
      monthlyPrice: 44.44,
      yearlyPrice: 399.99,
      yearlyMonthlyEquiv: 33.33,
      savingsPercent: 25,
      architectCredits: "444 credits/mo",
      features: [
        "Polymath BFN™ Calculator included",
        "Unlimited group chat users",
        "Voice notes 6 min",
        "20 hrs/mo meeting time",
      ],
    },
    {
      id: "proplus",
      name: "Pro+",
      tagline:
        "For those who consult OA daily and want deeper, more frequent access.",
      monthlyPrice: 88.88,
      yearlyPrice: 799.99,
      yearlyMonthlyEquiv: 66.66,
      savingsPercent: 25,
      architectCredits: "888 credits/mo",
      features: [
        "Polymath BFN™ Calculator included",
        "2× more OA credits than Pro",
        "Voice notes 8 min",
        "24 hrs/mo meeting time",
      ],
    },
    {
      id: "premium",
      name: "Premium",
      tagline:
        "For those who consult OA deeply and want priority access at all times.",
      monthlyPrice: 222.22,
      yearlyPrice: 1999.99,
      yearlyMonthlyEquiv: 166.66,
      savingsPercent: 25,
      architectCredits: "2,222 credits/mo",
      features: [
        "Polymath BFN™ Calculator included",
        "Priority access during peak",
        "Voice notes 10 min",
        "30 hrs/mo meeting time",
      ],
    },
  ] satisfies PlanConfig[]
).map((p) => ({
  ...p,
  annualSavings: Math.round((p.monthlyPrice * 12 - p.yearlyPrice) * 100) / 100,
}));

// ─── Plan limits ─────────────────────────────────────────────────────────────
// Single source of truth for all client-side feature limits per plan.
// Add new limits here; consume them via the usePlanLimits() hook.

export const PLAN_LIMITS = {
  /** Maximum voice note duration in milliseconds */
  voiceNoteDuration: {
    free: 60_000,
    standard: 180_000,
    pro: 360_000,
    proplus: 480_000,
    premium: 600_000,
  },
} satisfies Record<string, Record<SubscriptionPlan, number>>;

export interface FeatureRow {
  category: string;
  name: string;
  info?: string;
  hidden?: boolean;
  values: Record<SubscriptionPlan, string | boolean>;
}

// Plan rank: higher = more valuable. Used to determine upgrade vs downgrade direction.
// free=0, standard/mo=1, standard/yr=2, pro/mo=3, pro/yr=4,
// proplus/mo=5, proplus/yr=6, premium/mo=7, premium/yr=8
const PLAN_TIER: Record<SubscriptionPlan, number> = {
  free: 0,
  standard: 1,
  pro: 2,
  proplus: 3,
  premium: 4,
};

export function getPlanRank(
  plan: SubscriptionPlan,
  interval: BillingInterval,
): number {
  const tier = PLAN_TIER[plan];
  if (tier === 0) {
    return 0;
  } // free has no interval
  const intervalBonus = interval === "year" ? 1 : 0;
  return tier * 2 - 1 + intervalBonus;
}

export function formatPrice(price: number): string {
  if (price === 0) {
    return "$0";
  }
  return `$${price % 1 === 0 ? price : price.toFixed(2)}`;
}

export function isUpgrade(
  currentPlan: SubscriptionPlan,
  currentInterval: BillingInterval,
  targetPlan: SubscriptionPlan,
  targetInterval: BillingInterval,
): boolean {
  return (
    getPlanRank(targetPlan, targetInterval) >
    getPlanRank(currentPlan, currentInterval)
  );
}
